import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Layout from '../components/Layout'

function About() {
  const { t } = useTranslation()
  
  const features = [
    {
      icon: '🔐',
      title: t('about.privacy'),
      description: t('about.privacyDesc')
    },
    {
      icon: '⚡',
      title: t('about.instant'),
      description: t('about.instantDesc')
    },
    {
      icon: '🆓',
      title: t('about.free'),
      description: t('about.freeDesc')
    },
    {
      icon: '📱',
      title: t('about.mobile'),
      description: t('about.mobileDesc')
    },
    {
      icon: '🌙',
      title: t('about.darkModeFeature'),
      description: t('about.darkModeDesc')
    },
    {
      icon: '🔧',
      title: t('about.devFriendly'),
      description: t('about.devFriendlyDesc')
    }
  ]

  const tools = [
    { name: t('tools.urlEncode.name'), path: '/url-encode', description: t('tools.urlEncode.description') },
    { name: t('tools.base64.name'), path: '/base64', description: t('tools.base64.description') },
    { name: t('tools.jsonFormatter.name'), path: '/json-formatter', description: t('tools.jsonFormatter.description') },
    { name: t('tools.jwtDecoder.name'), path: '/jwt-decoder', description: t('tools.jwtDecoder.description') },
    { name: t('tools.qrCode.name'), path: '/qr-code', description: t('tools.qrCode.description') },
    { name: t('tools.uuidGenerator.name'), path: '/uuid-generator', description: t('tools.uuidGenerator.description') },
    { name: t('tools.hashGenerator.name'), path: '/hash-generator', description: t('tools.hashGenerator.description') },
  ]

  return (
    <Layout showBackButton title={t('about.title')}>
      <div className="p-4">
        {/* Home Link */}
        <Link 
          to="/"
          className="inline-flex items-center gap-2 text-primary hover:text-primary-600 transition-colors mb-6"
        >
          <span className="material-symbols-outlined text-xl">home</span>
          <span className="font-medium">{t('about.goToHome')}</span>
        </Link>

        {/* Hero */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-3">🛠️ AllinOneDecoder</h1>
          <p className="text-base text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            {t('about.subtitle')}
          </p>
        </div>

      {/* About Section */}
      <div className="card mb-8">
        <h2 className="text-2xl font-bold mb-4">{t('about.introduction')}</h2>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('about.introText1')}
        </p>
        <p className="text-gray-600 dark:text-gray-400">
          {t('about.introText2')}
        </p>
      </div>

      {/* Features Grid */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold mb-6 text-center">{t('about.features')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <div key={index} className="card hover:shadow-lg transition-shadow">
              <div className="text-4xl mb-4">{feature.icon}</div>
              <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Tools List */}
      <div className="card mb-8">
        <h2 className="text-2xl font-bold mb-6">{t('about.availableTools')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {tools.map((tool) => (
            <Link
              key={tool.path}
              to={tool.path}
              className="flex items-center gap-4 p-4 rounded-lg bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <div className="flex-1">
                <h3 className="font-semibold text-primary-600 dark:text-primary-400">{tool.name}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">{tool.description}</p>
              </div>
              <span className="text-gray-400">→</span>
            </Link>
          ))}
        </div>
      </div>

      {/* Privacy Section */}
      <div className="card mb-8">
        <h2 className="text-2xl font-bold mb-4">{t('about.privacyPolicy')}</h2>
        <div className="space-y-4 text-gray-600 dark:text-gray-400">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">{t('about.dataCollection')}</h3>
            <p>
              {t('about.dataCollectionText')}
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">{t('about.localStorage')}</h3>
            <p>
              {t('about.localStorageText')}
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">{t('about.cookies')}</h3>
            <p>
              {t('about.cookiesText')}
            </p>
          </div>
        </div>
      </div>

      {/* Tech Stack */}
      <div className="card mb-8">
        <h2 className="text-2xl font-bold mb-4">{t('about.techStack')}</h2>
        <div className="flex flex-wrap gap-3">
          {['React', 'TypeScript', 'Tailwind CSS', 'Vite', 'React Router'].map((tech) => (
            <span
              key={tech}
              className="px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full text-sm"
            >
              {tech}
            </span>
          ))}
        </div>
      </div>

      {/* Contact */}
      <div className="card text-center">
        <h2 className="text-2xl font-bold mb-4">{t('about.contact')}</h2>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('about.contactText')}
        </p>
        <a
          href="mailto:choidev92@gmail.com"
          className="btn-primary inline-flex items-center gap-2"
        >
          📧 {t('about.sendEmail')}
        </a>
      </div>
      </div>
    </Layout>
  )
}

export default About
